import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { IOSInstallBanner } from "@/components/IOSInstallBanner";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

const fmtUsd = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Cash-register chime (Web Audio, no asset needed)
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [
      { f: 1318.5, t: 0, d: 0.18 },
      { f: 1568.0, t: 0.12, d: 0.18 },
      { f: 2093.0, t: 0.24, d: 0.4 },
    ];
    notes.forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + d + 0.05);
    });
  } catch {
    /* audio blocked until first user gesture */
  }
}

export default function Register() {
  const [token, setToken] = useState(() => localStorage.getItem("register_token") || "");
  const [shop, setShop] = useState(() => localStorage.getItem("register_shop") || "");
  const [brand, setBrand] = useState(() => {
    try { return JSON.parse(localStorage.getItem("register_brand") || "null"); } catch { return null; }
  });
  const [merchantApproved, setMerchantApproved] = useState(() => localStorage.getItem("register_merchant") === "1");
  const [oversight, setOversight] = useState(() => localStorage.getItem("register_oversight") || "");
  const [loginForm, setLoginForm] = useState({ ledger_uuid: "", phone: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  // treasury-director oversight: portal key opens ANY approved merchant's register
  const [directorMode, setDirectorMode] = useState(false);
  const [directorForm, setDirectorForm] = useState({ key_id: "", passphrase: "" });
  const [merchantList, setMerchantList] = useState(null);
  const [directorKey, setDirectorKey] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [book, setBook] = useState(null);

  const loadBook = async () => {
    try {
      const res = await fetch(`${API}/register/books`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (res.ok) setBook(d);
    } catch { /* the book is read-only — the register keeps working without it */ }
  };

  const directorLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/portal/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(directorForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Director sign-in failed");
      const mres = await fetch(`${API}/portal/register/merchants`, {
        headers: { Authorization: `Bearer ${data.token}` },
      });
      const mdata = await mres.json();
      if (!mres.ok) throw new Error(typeof mdata.detail === "string" ? mdata.detail : "This key carries no directorate role");
      sessionStorage.setItem("director_portal_token", data.token);
      setDirectorKey(data.key_id);
      setMerchantList(mdata.merchants || []);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const openMerchantRegister = async (merchantUuid) => {
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/portal/register/access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("director_portal_token")}`,
        },
        body: JSON.stringify({ merchant_uuid: merchantUuid }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Register access denied");
      localStorage.setItem("register_token", data.token);
      localStorage.setItem("register_shop", data.label);
      localStorage.setItem("register_brand", JSON.stringify(data.brand || null));
      localStorage.setItem("register_merchant", "1");
      localStorage.setItem("register_oversight", data.director || "");
      setToken(data.token);
      setShop(data.label);
      setBrand(data.brand || null);
      setMerchantApproved(true);
      setOversight(data.director || "");
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const [amount, setAmount] = useState("");
  const [stage, setStage] = useState("keypad"); // keypad | qr | settled | guest
  const [activeRequest, setActiveRequest] = useState(null);
  const [settledInfo, setSettledInfo] = useState(null);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  // counter onboarding (approved merchants only): guest transactional wallet + cash-in
  const [guestForm, setGuestForm] = useState({ first_name: "", phone: "", cash: "" });
  const [guestBusy, setGuestBusy] = useState(false);
  const [guestResult, setGuestResult] = useState(null);
  const [guestError, setGuestError] = useState("");

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => () => stopPolling(), []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/wallet/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) {
        // detail can be a string, a structured object, or FastAPI's 422 array — always render words
        const d = data?.detail;
        const msg = typeof d === "string" ? d
          : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
          : (d?.message || d?.code) || "Sign-in failed — check the ledger UUID and phone";
        throw new Error(msg);
      }
      localStorage.setItem("register_token", data.token);
      localStorage.setItem("register_shop", data.label);
      localStorage.setItem("register_brand", JSON.stringify(data.brand || null));
      localStorage.setItem("register_merchant", data.merchant_approved ? "1" : "0");
      setToken(data.token);
      setShop(data.label);
      setBrand(data.brand || null);
      setMerchantApproved(!!data.merchant_approved);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const pressKey = (k) => {
    setError("");
    setAmount((prev) => {
      if (k === "C") return "";
      if (k === "←") return prev.slice(0, -1);
      if (k === "." && prev.includes(".")) return prev;
      if (prev.includes(".") && prev.split(".")[1]?.length >= 2) return prev;
      if (prev.replace(".", "").length >= 8) return prev;
      const next = prev + k;
      return next === "." ? "0." : next;
    });
  };

  const printSlip = () => {
    // printable counter slip of the live sale QR — customer scans with their camera,
    // the wallet opens with this exact bill pre-loaded, one Face ID tap settles it
    const svg = document.querySelector('[data-testid="register-qr"]')?.outerHTML;
    const win = window.open("", "_blank", "width=480,height=760");
    if (!win || !svg || !activeRequest) return;
    const shopName = (brand?.display_name || shop).replace(/</g, "&lt;");
    const amount = fmtUsd(activeRequest.amount_usd);
    win.document.write(`<html><head><title>Pay slip — ${shopName}</title><style>
      body{font-family:Georgia,serif;background:#fff;color:#000;display:flex;justify-content:center;margin:0;padding:24px}
      .slip{width:3.4in;border:2px solid #000;border-radius:14px;padding:22px;text-align:center}
      .brand{font-size:10px;letter-spacing:3px;text-transform:uppercase;color:#555}
      .amount{font-size:40px;font-weight:900;margin:10px 0}
      .qr{margin:12px auto;width:fit-content}
      .code{font-family:monospace;font-size:22px;letter-spacing:6px;font-weight:700}
      .steps{font-size:11px;line-height:1.6;color:#333;margin-top:14px;text-align:left}
      .foot{font-family:monospace;font-size:8px;letter-spacing:2px;color:#888;margin-top:16px}
    </style></head><body><div class="slip">
      <div class="brand">${shopName} · BOURG MERCHANT EXCHANGE</div>
      <div class="amount">${amount}</div>
      <div class="qr">${svg}</div>
      <div class="code">${activeRequest.code}</div>
      <div class="steps">1. Scan with your phone camera<br/>2. Your wallet opens with this bill loaded<br/>3. Approve with Face ID — settled instantly</div>
      <div class="foot">BGNZN.ORG · CHAIN 9940 · SETTLED T+0</div>
    </div><scr` + `ipt>window.onload=()=>{window.print();}</scr` + `ipt></body></html>`);
    win.document.close();
  };

  const charge = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      setError("Enter an amount first");
      return;
    }
    setError("");
    try {
      const res = await fetch(`${API}/wallet/pay-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ amount_usd: value, memo: `${shop} sale` }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not mint sale code");
      setActiveRequest(data);
      setStage("qr");
      // poll for settlement
      pollRef.current = setInterval(async () => {
        try {
          const check = await fetch(`${API}/wallet/pay-request/${data.code}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (check.ok) {
            const status = await check.json();
            if (status.status === "PAID") {
              stopPolling();
              setSettledInfo({ amount: value, payer: status.paid_by });
              setStage("settled");
              playChime();
              loadTape();
            }
          }
        } catch {
          /* keep polling */
        }
      }, 2500);
    } catch (err) {
      setError(err.message);
    }
  };

  const nextCustomer = () => {
    stopPolling();
    setActiveRequest(null);
    setSettledInfo(null);
    setAmount("");
    setGuestForm({ first_name: "", phone: "", cash: "" });
    setGuestResult(null);
    setGuestError("");
    setStage("keypad");
  };

  // today's tape — the register's box score, live every 15 seconds
  const [tape, setTape] = useState(null);
  const loadTape = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/register/sales`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTape(await res.json());
    } catch { /* the tape is additive — never block the register */ }
  }, [token]);
  useEffect(() => {
    loadTape();
    const t = setInterval(loadTape, 15000);
    return () => clearInterval(t);
  }, [loadTape]);

  const onboardGuest = async (e) => {
    e.preventDefault();
    setGuestBusy(true);
    setGuestError("");
    try {
      const res = await fetch(`${API}/register/guest-onboard`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: guestForm.phone,
          cash_usd: parseFloat(guestForm.cash) || 0,
          first_name: guestForm.first_name || undefined,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(
        typeof data.detail === "string" ? data.detail : data.detail?.message || "Could not open the guest wallet"
      );
      setGuestResult(data);
    } catch (err) {
      setGuestError(err.message);
    } finally {
      setGuestBusy(false);
    }
  };

  /* ---------- signed-out ---------- */
  if (!token) {
    return (
      <div data-testid="register-login" className="min-h-screen bg-stone-950 flex items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-4">
        <IOSInstallBanner label="Put this register on your iPhone" />
        <div className="w-full rounded-2xl border border-stone-800 bg-stone-900/60 p-8 space-y-5 backdrop-blur">
          <div className="space-y-1">
            <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">
              Bourg Merchant Exchange
            </span>
            <h1 className="text-2xl font-bold text-stone-100 display-serif">Merchant Register</h1>
            <p className="text-xs text-stone-400">
              {directorMode
                ? "Treasury director oversight — your portal key opens any approved register."
                : "Your shop's point of sale. Sign in with the shop's ledger UUID and registered phone."}
            </p>
          </div>

          {!directorMode ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-3">
                <input
                  data-testid="register-uuid-input"
                  type="text" required placeholder="Ledger UUID"
                  value={loginForm.ledger_uuid}
                  onChange={(e) => setLoginForm({ ...loginForm, ledger_uuid: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                />
                <input
                  data-testid="register-phone-input"
                  type="tel" required placeholder="Registered phone (+1 …)"
                  value={loginForm.phone}
                  onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
              </div>
              {loginError && <p data-testid="register-login-error" className="text-xs text-red-400">{loginError}</p>}
              <button
                data-testid="register-login-button"
                type="submit" disabled={loginBusy}
                className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {loginBusy ? "Opening register…" : "Open Register"}
              </button>
            </form>
          ) : !merchantList ? (
            <form onSubmit={directorLogin} className="space-y-5">
              <div className="space-y-3">
                <input
                  data-testid="director-key-input"
                  type="text" required placeholder="Portal key (BGNZN-…)"
                  value={directorForm.key_id}
                  onChange={(e) => setDirectorForm({ ...directorForm, key_id: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                />
                <input
                  data-testid="director-pass-input"
                  type="password" required placeholder="Cipher passphrase"
                  value={directorForm.passphrase}
                  onChange={(e) => setDirectorForm({ ...directorForm, passphrase: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
              </div>
              {loginError && <p data-testid="director-login-error" className="text-xs text-red-400">{loginError}</p>}
              <button
                data-testid="director-login-button"
                type="submit" disabled={loginBusy}
                className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {loginBusy ? "Verifying key…" : "Director Sign-In"}
              </button>
            </form>
          ) : (
            <div className="space-y-3" data-testid="director-merchant-picker">
              <p className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">
                {directorKey} — choose the register to open
              </p>
              {merchantList.length === 0 && (
                <p className="text-xs text-stone-400">No approved merchants yet.</p>
              )}
              {merchantList.map((m) => (
                <button
                  key={m.nexorone_ledger_uuid}
                  data-testid={`director-open-${m.nexorone_ledger_uuid}`}
                  onClick={() => openMerchantRegister(m.nexorone_ledger_uuid)}
                  disabled={loginBusy}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-left hover:border-amber-500 transition-colors disabled:opacity-50"
                >
                  <span className="block text-sm font-semibold text-stone-100">
                    {m.brand?.display_name || m.label}
                  </span>
                  <span className="block font-mono text-[10px] text-stone-500">{m.nexorone_ledger_uuid}</span>
                </button>
              ))}
              {loginError && <p data-testid="director-login-error" className="text-xs text-red-400">{loginError}</p>}
            </div>
          )}

          <button
            type="button"
            data-testid="register-mode-toggle"
            onClick={() => { setDirectorMode(!directorMode); setMerchantList(null); setLoginError(""); }}
            className="w-full text-center text-[11px] text-stone-500 hover:text-amber-400 transition-colors"
          >
            {directorMode ? "← merchant sign-in" : "Treasury director? Oversight sign-in →"}
          </button>
          <a
            data-testid="register-appstore-link"
            href="https://apps.apple.com/app/id6753765418"
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 pt-1 text-xs font-semibold text-stone-400 hover:text-amber-400 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"/>
            </svg>
            Prefer native? Get the iOS app
          </a>
        </div>
        </div>
      </div>
    );
  }

  /* ---------- register shell ---------- */
  return (
    <div data-testid="register-app" className="min-h-screen bg-stone-950 px-4 py-6">
      <div className="mx-auto w-full max-w-md space-y-5">
        <IOSInstallBanner label="Pin this register to your home screen" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand?.logo && (
              <img
                data-testid="register-brand-logo"
                src={brand.logo}
                alt={brand.display_name || shop}
                className="h-12 w-12 rounded-full border border-amber-500/40 object-cover"
              />
            )}
            <div>
              <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">Register</span>
              <div data-testid="register-shop-name" className="text-sm font-bold text-stone-200">{brand?.display_name || shop}</div>
              {oversight && (
                <div data-testid="register-oversight-badge" className="mono-tag text-[9px] uppercase tracking-widest text-sky-400">
                  Treasury oversight · {oversight}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="register-book-toggle"
              onClick={() => { const next = !bookOpen; setBookOpen(next); if (next) loadBook(); }}
              className="text-[10px] uppercase tracking-widest text-amber-500/80 hover:text-amber-300"
            >
              {bookOpen ? "Hide book" : "Today's book"}
            </button>
            <button
              data-testid="register-registry-toggle"
              onClick={() => setRegistryOpen(!registryOpen)}
              className="text-[10px] uppercase tracking-widest text-amber-500/80 hover:text-amber-300"
            >
              {registryOpen ? "Hide registry" : "Chain 9940"}
            </button>
            <button
              data-testid="register-logout-button"
              onClick={() => {
                stopPolling();
                localStorage.removeItem("register_token");
                localStorage.removeItem("register_shop");
                localStorage.removeItem("register_brand");
                localStorage.removeItem("register_merchant");
                localStorage.removeItem("register_oversight");
                sessionStorage.removeItem("director_portal_token");
                setToken("");
                setOversight("");
              }}
              className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-300"
            >
              Close register
            </button>
          </div>
        </div>

        {/* the daily book — card, Zelle, wallet and QR sales side by side, written as they settle */}
        {bookOpen && (
          <div data-testid="register-book" className="rounded-2xl border border-stone-800 bg-stone-900/60 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">Today's book · {book?.day}</span>
              <button data-testid="register-book-refresh" onClick={loadBook}
                className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-300">Refresh</button>
            </div>
            <div className="grid grid-cols-4 gap-2" data-testid="register-book-rails">
              {["CARD", "ZELLE", "WALLET", "PAY_REQUEST"].map((r) => {
                const b = book?.rails?.[r];
                return (
                  <div key={r} className="rounded-lg border border-stone-800 p-2 text-center" data-testid={`book-rail-${r.toLowerCase()}`}>
                    <div className="text-[9px] uppercase tracking-widest text-stone-500">{r === "PAY_REQUEST" ? "QR" : r}</div>
                    <div className="text-sm font-bold text-stone-100">{b ? fmtUsd(b.total_usd) : "—"}</div>
                    <div className="text-[9px] text-stone-600">{b ? `${b.count} sale${b.count === 1 ? "" : "s"}` : ""}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-stone-500">Wallet right now</span>
              <span className="text-sm font-bold text-stone-100" data-testid="register-book-balance">
                {book?.wallet_balance_usd != null ? fmtUsd(book.wallet_balance_usd) : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-stone-800 pt-2">
              <span className="text-[10px] uppercase tracking-widest text-stone-500">Day total</span>
              <span className="text-lg font-extrabold text-emerald-300" data-testid="register-book-total">{book ? fmtUsd(book.total_usd) : "—"}</span>
            </div>
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {(book?.sales || []).map((s, i) => (
                <div key={i} data-testid="register-book-row" className="flex items-center justify-between text-[11px] font-mono border-b border-stone-800/50 pb-1">
                  <span className="text-stone-500">{(s.created_at || "").slice(11, 16)}</span>
                  <span className="text-amber-500/80">{s.rail}{s.env && s.env !== "live" ? "·test" : ""}</span>
                  <span className="text-stone-300 flex-1 mx-2 truncate">{s.memo}</span>
                  <span className="text-emerald-300">{fmtUsd(s.amount_usd)}</span>
                </div>
              ))}
              {book && book.count === 0 && (
                <p className="text-[11px] text-stone-600 text-center py-2">No sales settled yet today — the tape writes itself as they land.</p>
              )}
            </div>
          </div>
        )}

        {/* keypad stage */}
        {stage === "keypad" && (
          <div data-testid="register-keypad-stage" className="space-y-4">
            <div className="rounded-2xl border border-stone-800 bg-stone-900/60 p-6 text-center">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Sale amount</div>
              <div data-testid="register-amount-display" className="text-5xl font-extrabold text-stone-100 display-serif min-h-[3.5rem]">
                {amount ? `$${amount}` : "$0.00"}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {["1","2","3","4","5","6","7","8","9",".","0","←"].map((k) => (
                <button
                  key={k}
                  data-testid={`keypad-${k === "←" ? "back" : k === "." ? "dot" : k}`}
                  onClick={() => pressKey(k)}
                  className="rounded-xl border border-stone-800 bg-stone-900/60 py-5 text-xl font-bold text-stone-100 hover:border-amber-500/50 active:bg-stone-800 transition-colors"
                >
                  {k}
                </button>
              ))}
            </div>
            {error && <p data-testid="register-error" className="text-xs text-red-400 text-center">{error}</p>}
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="keypad-clear"
                onClick={() => setAmount("")}
                className="rounded-xl border border-stone-700 py-4 text-sm font-bold text-stone-300"
              >
                Clear
              </button>
              <button
                data-testid="register-charge-button"
                onClick={charge}
                className="rounded-xl bg-amber-500 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 transition-colors"
              >
                Charge
              </button>
            </div>
            {merchantApproved && (
              <button
                data-testid="guest-open-button"
                onClick={() => { setStage("guest"); setGuestResult(null); setGuestError(""); }}
                className="w-full rounded-xl border border-emerald-500/40 bg-emerald-950/20 py-4 text-sm font-bold text-emerald-300 hover:bg-emerald-950/40 transition-colors"
              >
                New customer — open a wallet
              </button>
            )}
            {tape && (
              <div data-testid="sales-tape" className="rounded-2xl border border-stone-800 bg-stone-900/60 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-amber-500/80 font-mono">Today's tape · {tape.date}</p>
                  <p className="text-[9px] font-mono text-stone-500">live · 15s</p>
                </div>
                {tape.sales.length === 0 ? (
                  <p data-testid="tape-empty" className="text-xs text-stone-500 text-center py-3">No buckets yet — the first sale of the day lands here.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-[3.4rem_1fr_4.5rem] gap-2 px-1 pb-1 border-b border-stone-800 font-mono text-[10px] text-stone-500">
                      <span>TIME</span><span>PAYER · PLAY</span><span className="text-right">PTS</span>
                    </div>
                    <div className="max-h-44 overflow-y-auto">
                      {tape.sales.map((s, i) => (
                        <div key={s.settlement_tx} data-testid={`tape-row-${i}`} className="grid grid-cols-[3.4rem_1fr_4.5rem] gap-2 px-1 py-1.5 border-b border-stone-800/50 font-mono text-[11px]">
                          <span className="text-stone-500">{s.local_time}</span>
                          <span className="text-stone-200 truncate">{s.is_tip && <span className="text-sky-400 font-bold">TIP · </span>}{s.cardholder_label} <span className="text-stone-500">· {s.memo}</span></span>
                          <span className={`text-right font-bold ${s.is_tip ? "text-sky-400" : "text-emerald-400"}`}>{fmtUsd(s.amount_usd)}</span>
                        </div>
                      ))}
                    </div>
                    <div data-testid="tape-totals" className="grid grid-cols-5 gap-2 mt-3 text-center">
                      <div><p className="font-mono text-sm font-bold text-stone-100" data-testid="tape-count">{tape.totals.count}</p><p className="text-[9px] font-mono text-stone-500 uppercase">buckets</p></div>
                      <div><p className="font-mono text-sm font-bold text-emerald-400" data-testid="tape-gross">{fmtUsd(tape.totals.gross_usd)}</p><p className="text-[9px] font-mono text-stone-500 uppercase">points</p></div>
                      <div><p className="font-mono text-sm font-bold text-sky-400" data-testid="tape-tips">{fmtUsd(tape.totals.tips_usd || 0)}</p><p className="text-[9px] font-mono text-stone-500 uppercase">tips</p></div>
                      <div><p className="font-mono text-sm font-bold text-stone-100" data-testid="tape-avg">{fmtUsd(tape.totals.avg_usd)}</p><p className="text-[9px] font-mono text-stone-500 uppercase">avg</p></div>
                      <div><p className="font-mono text-sm font-bold text-amber-400" data-testid="tape-high">{fmtUsd(tape.totals.biggest_usd)}</p><p className="text-[9px] font-mono text-stone-500 uppercase">high</p></div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* guest onboarding stage (approved merchants only) */}
        {stage === "guest" && (
          <div data-testid="register-guest-stage" className="rounded-2xl border border-emerald-500/40 bg-stone-900/60 p-6 space-y-4">
            {!guestResult ? (
              <form onSubmit={onboardGuest} className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">New customer</div>
                  <p className="text-xs text-stone-400 mt-1">
                    Take their payment — notes or any medium you accept — enter their phone and the
                    value received; their wallet opens funded in seconds.
                    A guest wallet is for purchases only and is <span className="text-stone-200 font-semibold">not</span> a league membership.
                  </p>
                </div>
                <input
                  data-testid="guest-name-input"
                  type="text" placeholder="First name (optional)"
                  value={guestForm.first_name}
                  onChange={(e) => setGuestForm({ ...guestForm, first_name: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-emerald-500 focus:outline-none"
                />
                <input
                  data-testid="guest-phone-input"
                  type="tel" required placeholder="Customer's mobile phone"
                  value={guestForm.phone}
                  onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-emerald-500 focus:outline-none"
                />
                <input
                  data-testid="guest-cash-input"
                  type="number" step="0.01" min="0" placeholder="Value received (USD)"
                  value={guestForm.cash}
                  onChange={(e) => setGuestForm({ ...guestForm, cash: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-emerald-500 focus:outline-none"
                />
                {guestError && <p data-testid="guest-error" className="text-xs text-red-400">{guestError}</p>}
                <button
                  data-testid="guest-submit-button"
                  type="submit" disabled={guestBusy}
                  className="w-full rounded-lg bg-emerald-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-emerald-400 disabled:opacity-50"
                >
                  {guestBusy ? "Opening wallet…" : "Open guest wallet"}
                </button>
                <button
                  data-testid="guest-cancel-button"
                  type="button" onClick={nextCustomer}
                  className="w-full text-xs text-stone-500 underline hover:text-stone-300"
                >
                  Back to register
                </button>
              </form>
            ) : (
              <div className="space-y-4 text-center">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">Guest wallet ready</div>
                {guestResult.cash_credited_usd > 0 && (
                  <div data-testid="guest-funded-amount" className="text-3xl font-extrabold text-emerald-300 display-serif">
                    {fmtUsd(guestResult.cash_credited_usd)} loaded
                  </div>
                )}
                <div className="mx-auto w-fit rounded-xl bg-white p-4">
                  <QRCodeSVG
                    data-testid="guest-wallet-qr"
                    value={`${window.location.origin}/wallet?uuid=${guestResult.ledger_uuid}`}
                    size={200}
                  />
                </div>
                <p className="text-xs text-stone-400">
                  Customer scans with their phone camera — the wallet opens with their details
                  pre-filled; they just enter their phone number.
                </p>
                <div data-testid="guest-uuid" className="rounded-lg bg-stone-950 px-3 py-2 text-[11px] font-mono text-amber-400 break-all select-all">
                  {guestResult.ledger_uuid}
                </div>
                <p data-testid="guest-sms-status" className="text-[10px] text-emerald-400/90">
                  {guestResult.welcome_sms === "SENT"
                    ? "Wallet link texted to their phone — they tap it and sign in."
                    : "No text sent (carrier rules) — have them scan the QR above."}
                </p>
                <p className="text-[10px] text-stone-500 leading-relaxed">{guestResult.member_notice}</p>
                <button
                  data-testid="guest-done-button"
                  onClick={nextCustomer}
                  className="w-full rounded-xl bg-amber-500 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 transition-colors"
                >
                  Done — next customer
                </button>
              </div>
            )}
          </div>
        )}

        {/* QR stage */}
        {stage === "qr" && activeRequest && (
          <div data-testid="register-qr-stage" className="rounded-2xl border border-amber-500/40 bg-stone-900/60 p-6 space-y-4 text-center">
            {brand?.logo && (
              <img src={brand.logo} alt={brand.display_name || shop} className="mx-auto h-16 w-16 rounded-full border border-amber-500/40 object-cover" />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Customer scans to pay</div>
              <div data-testid="register-charge-amount" className="text-4xl font-extrabold text-stone-100 display-serif">
                {fmtUsd(activeRequest.amount_usd)}
              </div>
            </div>
            <div className="mx-auto w-fit rounded-xl bg-white p-4">
              <QRCodeSVG
                data-testid="register-qr"
                value={`${window.location.origin}/wallet?pay=${activeRequest.code}`}
                size={220}
              />
            </div>
            <div data-testid="register-code" className="text-2xl font-mono font-bold tracking-[0.3em] text-amber-400">
              {activeRequest.code}
            </div>
            <p className="text-xs text-stone-400">
              Waiting for payment… customer scans with their camera, enters the code in their wallet, or pays from anywhere via the text link.
            </p>
            <a
              data-testid="register-text-link"
              href={`sms:?&body=${encodeURIComponent(
                `${brand?.display_name || shop}: your total is ${fmtUsd(activeRequest.amount_usd)}. Tap to pay from your BMEX wallet (Face ID): ${window.location.origin}/wallet?pay=${activeRequest.code}`
              )}`}
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-black font-bold text-xs uppercase tracking-wider px-5 py-2.5 hover:bg-amber-400 transition-colors"
            >
              Text payment link to customer
            </a>
            <div className="flex items-center justify-center gap-5">
              <button
                data-testid="register-print-slip"
                onClick={printSlip}
                className="text-xs text-amber-500 underline hover:text-amber-400"
              >
                Print counter slip
              </button>
              <button
                data-testid="register-cancel-sale"
                onClick={nextCustomer}
                className="text-xs text-stone-500 underline hover:text-stone-300"
              >
                Cancel sale
              </button>
            </div>
          </div>
        )}

        {/* settled stage */}
        {stage === "settled" && settledInfo && (
          <div data-testid="register-settled-stage" className="rounded-2xl border border-emerald-500/50 bg-emerald-950/30 p-8 space-y-4 text-center">
            {brand?.logo && (
              <img src={brand.logo} alt={brand.display_name || shop} className="mx-auto h-20 w-20 rounded-full border-2 border-emerald-500/50 object-cover" />
            )}
            <div className="text-4xl">✓</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Payment received · settled T+0</div>
            <div data-testid="register-settled-amount" className="text-5xl font-extrabold text-emerald-300 display-serif">
              {fmtUsd(settledInfo.amount)}
            </div>
            <button
              data-testid="register-next-customer"
              onClick={nextCustomer}
              className="w-full rounded-xl bg-amber-500 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 transition-colors"
            >
              Next customer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
